// Componente TerminosCondicionesCompras
// Página de términos y condiciones de compras con Mercado Pago

import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import Button from '../../components/UI/Button';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';

/**
 * Componente TerminosCondicionesCompras
 * 
 * Muestra los términos y condiciones de compras en Amarsi
 * Adaptado para productos virtuales de apoyo al proyecto Club Amarsi
 * Cumple con los requisitos de Mercado Pago para comercio electrónico
 */
const TerminosCondicionesCompras = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-900">
      {/* Header */}
      <header className="bg-white dark:bg-neutral-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/" className="flex items-center text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors">
                <FiArrowLeft className="h-5 w-5 mr-2" />
                Volver al inicio
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <AmarsiLogo width='2rem' height='2rem'/>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Amarsi</h1>
            </div>
            <nav className="flex items-center space-x-4">
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Privacidad
              </Link>
              <Link to="/login">
                <Button variant="primary" size="sm">
                  Iniciar Sesión
                </Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Contenido principal */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="bg-white dark:bg-neutral-800 rounded-lg shadow-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Términos y Condiciones de Compra <small className="text-lg font-normal"> Actualizado el 2026-07-10</small>
            </h1>
            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
              Estos términos y condiciones rigen las compras realizadas en la plataforma Amarsi a través de 
              <strong> Mercado Pago</strong>. Al realizar una compra, usted acepta estos términos y comprende la 
              naturaleza de los productos que adquiere.
            </p>
          </div>

          <div className="space-y-8 text-gray-700 dark:text-gray-300">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">1. Naturaleza de los Productos</h2>
              <p className="mb-4">
                Los productos ofrecidos en Amarsi son <strong>productos virtuales</strong> diseñados para apoyar el 
                proyecto <strong>Club Amarsi</strong>. Estos productos no son físicos ni reales, y su propósito es 
                generar fondos para mejorar, crecer y ofrecer un mejor servicio a la comunidad.
              </p>
              
              <div className="bg-violet-50 dark:bg-violet-900/20 p-4 rounded-lg border border-violet-200 dark:border-violet-700 mb-4">
                <h3 className="font-semibold text-violet-800 dark:text-violet-200 mb-2">Características de los Productos Virtuales:</h3>
                <ul className="list-disc pl-6 space-y-1 text-violet-700 dark:text-violet-300">
                  <li><strong>Entrega inmediata:</strong> Una vez aprobado el pago, el acceso al producto se activa automáticamente</li>
                  <li><strong>Sin envío físico:</strong> No hay productos físicos para enviar ni recibir</li>
                  <li><strong>Sin stock limitado:</strong> Al ser virtuales, no hay limitaciones de inventario</li>
                  <li><strong>Acceso revocable:</strong> En caso de reembolso, el acceso al producto puede ser revocado</li>
                </ul>
              </div>

              <p>
                Los fondos generados por estas compras se utilizan exclusivamente para: mejora de infraestructura, 
                desarrollo de nuevas funcionalidades, mantenimiento del servicio, soporte al usuario y crecimiento del proyecto.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">2. Procesamiento de Pagos con Mercado Pago</h2>
              
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">2.1 Medios de Pago</h3>
              <p>
                Aceptamos pagos a través de Mercado Pago utilizando diversos medios: tarjetas de crédito, tarjetas de débito, 
                dinero en cuenta de Mercado Pago, y otros métodos disponibles según su país.
              </p>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">2.2 Seguridad de Pagos</h3>
              <p>
                Todas las transacciones se procesan de forma segura a través de la plataforma de Mercado Pago. 
                Amarsi no almacena ni tiene acceso a la información sensible de sus tarjetas de crédito o débito. 
                El procesamiento de pagos cumple con los estándares de seguridad PCI DSS.
              </p>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">2.3 Confirmación de Pago</h3>
              <p>
                Una vez que el pago es aprobado por Mercado Pago, recibirá una confirmación por correo electrónico 
                y el acceso al producto virtual se activará automáticamente en su cuenta de Amarsi.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">3. Compra Protegida de Mercado Pago</h2>
              <p className="mb-4">
                Todas las compras realizadas a través de Mercado Pago cuentan con el programa de <strong>Compra Protegida</strong>, 
                que brinda las siguientes garantías:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Protección contra fraude:</strong> Respaldo inmediato ante movimientos no autorizados en su cuenta
                </li>
                <li>
                  <strong>Resolución de disputas:</strong> Mecanismos de mediación para resolver conflictos entre comprador y vendedor
                </li>
                <li>
                  <strong>Plazo de reclamo:</strong> Puede iniciar un reclamo dentro de los 14 días corridos desde la fecha de la compra
                </li>
              </ul>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-700 mt-4">
                <p className="text-yellow-800 dark:text-yellow-200">
                  <strong>Nota importante:</strong> La Compra Protegida de Mercado Pago protege contra fraudes y pagos no autorizados. 
                  Debido a la naturaleza virtual de nuestros productos, los reembolsos por otros motivos están sujetos a las 
                  condiciones específicas descritas en la sección 5.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">4. Política de Reembolsos</h2>
              
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">4.1 Casos Elegibles para Reembolso</h3>
              <p className="mb-4">
                Debido a la naturaleza virtual de los productos, los reembolsos solo se consideran en los siguientes casos:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Pago no autorizado:</strong> Si el pago fue realizado sin su consentimiento (protegido por Compra Protegida)
                </li>
                <li>
                  <strong>Error técnico:</strong> Si el producto no se entregó correctamente debido a un error técnico de nuestra plataforma
                </li>
                <li>
                  <strong>Descripción incorrecta:</strong> Si el producto recibido difiere significativamente de lo descrito en la página de compra
                </li>
                <li>
                  <strong>Duplicación de pago:</strong> Si fue cobrado más de una vez por el mismo producto
                </li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">4.2 Casos NO Elegibles para Reembolso</h3>
              <p className="mb-4">
                No se otorgan reembolsos en los siguientes casos:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Cambio de opinión:</strong> Si simplemente decidió que no quiere el producto después de recibirlo
                </li>
                <li>
                  <strong>Uso del producto:</strong> Si ya utilizó o accedió al contenido del producto virtual
                </li>
                <li>
                  <strong>Conocimiento previo:</strong> Si conocía la naturaleza del producto antes de la compra
                </li>
              </ul>

              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 mt-4">4.3 Proceso de Solicitud de Reembolso</h3>
              <p>
                Para solicitar un reembolso, debe:
              </p>
              <ol className="list-decimal pl-6 mt-2 space-y-1">
                <li>Contactarnos a <a href="mailto:help@clubamarsi.com" className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300">help@clubamarsi.com</a> dentro de los 14 días desde la compra</li>
                <li>Proporcionar su ID de orden y detalles del problema</li>
                <li>Incluir evidencia del caso (capturas de pantalla, correos de confirmación, etc.)</li>
                <li>Esperar la revisión de su caso (generalmente 3-5 días hábiles)</li>
              </ol>

              <p className="mt-4">
                Si su solicitud es aprobada, el reembolso se procesará a través de Mercado Pago y el dinero será devuelto 
                al mismo método de pago utilizado originalmente. El tiempo de procesamiento depende de su banco y de Mercado Pago.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">5. Proceso de Reclamo con Mercado Pago</h2>
              <p className="mb-4">
                Si necesita iniciar un reclamo a través de la Compra Protegida de Mercado Pago, siga estos pasos:
              </p>
              
              <ol className="list-decimal pl-6 space-y-2">
                <li>
                  <strong>Conservar comprobantes:</strong> Guarde el correo de confirmación de compra y cualquier evidencia relevante
                </li>
                <li>
                  <strong>Reportar dentro del plazo:</strong> Inicie el reclamo dentro de los 14 días corridos desde la fecha de compra
                </li>
                <li>
                  <strong>Usar su cuenta de Mercado Pago:</strong> El reclamo debe iniciarse desde su cuenta de Mercado Pago compradora
                </li>
                <li>
                  <strong>Seguir el proceso:</strong> Complete el formulario de reclamo y presente la evidencia requerida
                </li>
                <li>
                  <strong>Mantener cuenta activa:</strong> Su cuenta de Mercado Pago debe estar activa durante todo el proceso de reclamo
                </li>
              </ol>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">6. Responsabilidades del Usuario</h2>
              <p className="mb-4">
                Al realizar una compra en Amarsi, usted se compromete a:
              </p>
              
              <ul className="list-disc pl-6 space-y-2">
                <li>Proporcionar información veraz y precisa durante el proceso de compra</li>
                <li>Mantener segura su cuenta y contraseña de Amarsi</li>
                <li>Revisar cuidadosamente la descripción del producto antes de comprar</li>
                <li>Entender que los productos son virtuales y no físicos</li>
                <li>No compartir ni distribuir el contenido de los productos virtuales adquiridos</li>
                <li>Respetar los términos de uso de la plataforma Amarsi</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">7. Modificaciones a los Términos</h2>
              <p>
                Amarsi se reserva el derecho de modificar estos términos y condiciones en cualquier momento. 
                Las modificaciones entrarán en vigor en el momento de su publicación en esta página. 
                Se recomienda revisar periódicamente estos términos para estar informado de cualquier cambio.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">8. Contacto y Soporte</h2>
              <p className="mb-4">
                Si tiene preguntas, inquietudes o necesita asistencia con su compra, puede contactarnos:
              </p>
              
              <div className="bg-violet-50 dark:bg-violet-900/20 p-4 rounded-lg border border-violet-200 dark:border-violet-700">
                <p className="text-violet-800 dark:text-violet-200 mb-2">
                  <strong>Correo electrónico:</strong>{' '}
                  <a href="mailto:help@clubamarsi.com" className="font-semibold underline">
                    help@clubamarsi.com
                  </a>
                </p>
                <p className="text-violet-800 dark:text-violet-200">
                  <strong>Tiempo de respuesta:</strong> Generalmente 24-48 horas hábiles
                </p>
              </div>

              <p className="mt-4">
                Para consultas específicas sobre pagos y reembolsos a través de Mercado Pago, también puede contactar 
                directamente al soporte de Mercado Pago desde su cuenta.
              </p>
            </section>

            <section className="bg-gray-50 dark:bg-gray-900/20 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Información Adicional</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-2">
                Estos términos y condiciones complementan nuestra Política de Privacidad y los Términos de Servicio 
                generales de Amarsi. En caso de conflicto, estos términos prevalecerán específicamente para las compras.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                Al realizar una compra, usted declara haber leído, comprendido y aceptado estos términos y condiciones.
              </p>
            </section>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-neutral-800 mt-4 border-t border-gray-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <AmarsiLogo width='2rem' height='2rem'/>
              <span className="text-gray-900 dark:text-white font-semibold">Amarsi</span>
              <span className="text-gray-500">© {new Date().getFullYear()} Amarsi. Todos los derechos reservados.</span>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              >
                Privacidad
              </Link>
              <Link 
                to="/terminos-compras" 
                className="text-gray-600 dark:text-white font-medium"
              >
                Términos de Compra
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TerminosCondicionesCompras;
